import { Avatar, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Checkbox from '@brainpool/components/molecules/Checkbox/Checkbox';
import Table, { HeaderCellProps, TableRowProps } from '@brainpool/components/organisms/Table/Table';
import { useEffect, useState, Activity, useMemo, useCallback } from 'react';

import { Case } from '../../types/Case';
import { ColumnId, ColumnProps, defaultSortConfig } from './types';
import {
  getFoundersString,
  getStageIcon,
  isProjectDisabled,
  triggerFitScoreSurvey,
  fitScoreFilter,
  newFilter,
  sharedFilter,
  completionFilter,
} from './functions';
import { useNavigate } from 'react-router-dom';
import { ShareProject } from '@brainpool/icon-pack';
import { truncateWords } from '@brainpool/utils/helpers/truncate';
import FitScore from '../FitScore/FitScore';
import DataCoverage from '../DataCoverage/DataCoverage';
import { getRiskAnalysis } from '../RiskIcon/getRiskAnalysis';
import ButtonStack from './ButtonStack/ButtonStack';
import { ModalProps } from '../../types/ModalTypes';
import { getSavedColumns } from '../../utils/getSavedColumns';
import Button from '@brainpool/components/atoms/Button/Button';
import BulkActions from './BulkActions/BulkActions';
import { handleGoToProject } from '../../utils/commonFunctions';
import { Group } from '../../types/GroupTypes';
import { User } from '../../types/User';
import { reactiveVars } from '@brainpool/apollo-connect';

export interface ProjectsTableProps {
  filter: string;
  data: Case[];
  allUsers: Group[];
  user: User | null;
  loading: boolean;
  refresh?: () => void;
}

export function ProjectsTable({
  filter,
  data,
  allUsers,
  user,
  loading,
  refresh,
}: ProjectsTableProps) {
  //hooks
  const theme = useTheme();
  const navigate = useNavigate();

  // data from cache
  const tableData: Case[] = useMemo(
    () => (data.filter((item): item is Case => !!item) as Case[]) || [],
    [data]
  );
  const { org_id, survey_responses, id: userId, user_preferences } = user ?? {};

  // column helpers
  const table = useMemo(() => user_preferences?.table ?? [], [user_preferences?.table]);
  const savedColumns = useMemo(() => getSavedColumns(table), [table]);
  const getSavedColumnById = (id: ColumnId): ColumnProps | undefined =>
    savedColumns.find(col => col.id === id);

  const filteredData = useMemo(() => {
    switch (filter) {
      case 'completion':
        return completionFilter(tableData);
      case 'new':
        return newFilter(tableData);
      case 'shared':
        return sharedFilter(tableData);
      case 'fitScore':
        return fitScoreFilter(tableData);
      default:
        return tableData;
    }
  }, [filter, tableData]);

  // state
  const [tableColData, setTableColData] = useState<TableRowProps[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleOpenModal = (data: Partial<ModalProps>) => {
    reactiveVars.modalData({
      ...data,
      open: true,
    });
  };
  // selected case handlers
  const caseIds = useMemo(() => tableColData.map(row => row.id), [tableColData]);
  const allSelected = caseIds.length > 0 && caseIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const onSelectAllClick = () => setSelectedIds(allSelected ? new Set() : new Set(caseIds));

  const updateSelectedCases = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // setup table data
  const computedTableData = useMemo(
    () =>
      filteredData && filteredData.length > 0
        ? filteredData.map((item: Case) => {
            const { id, role, workflow_state, details, enrichments } = item;
            const { status, updated_at, data_availability, risks } = workflow_state ?? {};
            const { name, created_by, created_date } = details ?? {};
            const { investment_stage, patents } = enrichments ?? {};

            const projectDisabled = isProjectDisabled(status, updated_at);
            const patentsString = patents === true ? 'Yes' : patents === false ? 'No' : '';

            const allItems: TableRowProps['items'] = [
              {
                // Placeholder for checkbox - will be replaced in dataWithSelection memo
                item: null,
              },
              {
                item: (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexDirection: 'row',
                    }}
                  >
                    <Tooltip title={`View ${name}`}>
                      <Button
                        variant="text"
                        data-testid={`project-link-${id}`}
                        disabled={projectDisabled}
                        onClick={() => {
                          if (projectDisabled) return;
                          handleGoToProject(id, name, 'projects', navigate);
                        }}
                        sx={{
                          padding: 0,
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        {name}
                      </Button>
                    </Tooltip>
                    {investment_stage ? (
                      <Tooltip title={'Investment Stage: ' + investment_stage}>
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: 'transparent',
                            color: 'success.main',
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                        >
                          <Typography fontSize={12} fontWeight={700}>
                            {getStageIcon(investment_stage)}
                          </Typography>
                        </Avatar>
                      </Tooltip>
                    ) : (
                      ''
                    )}
                    <Activity mode={role?.toLowerCase() !== 'owner' ? 'visible' : 'hidden'}>
                      <Tooltip
                        data-testid={`shared-icon-${id}`}
                        title={
                          <div style={{ whiteSpace: 'pre-line' }}>{`Shared by ${
                            allUsers?.[0]?.members?.find(user => user?.id === created_by)
                              ?.identifier || created_by
                          }\nAccess Level: ${role === 'Editor' ? 'Edit' : 'View'}`}</div>
                        }
                      >
                        <Box>
                          <ShareProject size={16} fill={theme.palette.text.primary} />
                        </Box>
                      </Tooltip>
                    </Activity>
                  </Box>
                ),
                sortValue: name,
              },
              {
                item: (
                  <Typography fontWeight={500}>
                    {new Date(created_date).toLocaleDateString()}
                  </Typography>
                ),
                sortValue: created_date,
              },
              ...[
                {
                  item: enrichments?.sector && (
                    <Tooltip title={enrichments?.sector}>
                      <Chip
                        label={truncateWords({ str: enrichments?.sector })}
                        sx={{
                          bgcolor: 'info.light',
                        }}
                      />
                    </Tooltip>
                  ),

                  showItem: getSavedColumnById('sector')?.display,
                  order: getSavedColumnById('sector')?.order,
                },
                {
                  item: enrichments?.fit_score && <FitScore {...enrichments?.fit_score} />,

                  sortValue: enrichments?.fit_score?.score,
                  showItem: getSavedColumnById('fitScore')?.display,
                  order: getSavedColumnById('fitScore')?.order,
                },
                {
                  item: enrichments?.usp ? (
                    <Tooltip title={enrichments?.usp}>
                      <Chip
                        label={truncateWords({ str: enrichments?.usp })}
                        sx={{
                          bgcolor: 'info.light',
                        }}
                      />
                    </Tooltip>
                  ) : null,

                  showItem: getSavedColumnById('ups')?.display,
                  order: getSavedColumnById('ups')?.order,
                },
                {
                  item: enrichments?.key_technologies ? (
                    <Tooltip title={enrichments?.key_technologies}>
                      <Typography>
                        {truncateWords({ str: enrichments?.key_technologies })}
                      </Typography>
                    </Tooltip>
                  ) : null,

                  showItem: getSavedColumnById('tech')?.display,
                  order: getSavedColumnById('tech')?.order,
                },
                {
                  item: <Typography fontWeight={500}>{patentsString}</Typography>,

                  sortValue: patentsString,
                  showItem: getSavedColumnById('patents')?.display,
                  order: getSavedColumnById('patents')?.order,
                },
                {
                  item:
                    enrichments?.founders?.length > 0 ? (
                      <Tooltip
                        title={
                          <div style={{ whiteSpace: 'pre-line' }}>
                            {getFoundersString(enrichments)}
                          </div>
                        }
                      >
                        <Typography>
                          {truncateWords({
                            str: getFoundersString(enrichments),
                          })}
                        </Typography>
                      </Tooltip>
                    ) : null,

                  showItem: getSavedColumnById('team')?.display,
                  order: getSavedColumnById('team')?.order,
                },
                {
                  item: (
                    <Activity
                      mode={
                        data_availability?.combined && !isNaN(data_availability?.combined)
                          ? 'visible'
                          : 'hidden'
                      }
                    >
                      <DataCoverage
                        percentage={data_availability?.combined}
                        workflow_state={workflow_state?.status}
                      />
                    </Activity>
                  ),
                  sortValue: data_availability?.combined,
                  showItem: getSavedColumnById('dataCoverage')?.display,
                  order: getSavedColumnById('dataCoverage')?.order,
                },
                {
                  item: (
                    <>
                      {risks &&
                        getRiskAnalysis({
                          risks,
                          loading,
                        })}
                    </>
                  ),
                  sortValue:
                    risks && typeof risks === 'object'
                      ? `${(risks?.critical?.length || 0).toString().padStart(6, '0')}-${(
                          risks?.standard?.length || 0
                        )
                          .toString()
                          .padStart(6, '0')}`
                      : '000000-000000',
                  showItem: getSavedColumnById('risks')?.display,
                  order: getSavedColumnById('risks')?.order,
                },
              ]
                .filter(col => col.showItem !== false)
                .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)),
            ];
            return {
              id,
              items: allItems,
              disabled: projectDisabled,
            };
          })
        : [],
    [filteredData, savedColumns, allUsers, loading]
  );

  useEffect(() => {
    setTableColData(computedTableData);
  }, [computedTableData]);

  // Memoize table data with selection state to avoid full rebuild
  const dataWithSelection = useMemo(() => {
    return tableColData.map(row => ({
      ...row,
      items: row?.items?.map((item, idx) => {
        // First column is checkbox
        if (idx === 0) {
          const isChecked = selectedIds.has(row.id);
          return {
            ...item,
            item: (
              <Checkbox
                id={`check-${row.id}`}
                key={`check-${row.id}`}
                data-testid={`check-${row.id}`}
                color="primary"
                checked={row.disabled ? undefined : isChecked}
                onChange={row.disabled ? undefined : () => updateSelectedCases(row.id)}
                disabled={row.disabled}
              />
            ),
          };
        }
        return item;
      }),
    }));
  }, [tableColData, selectedIds]);

  return (
    <Box data-testid="projects-table">
      <ButtonStack
        onRefresh={() => refresh && refresh()}
        onCustomise={() => {
          handleOpenModal({ type: 'customise' });
        }}
      />
      <BulkActions
        onClose={() => setSelectedIds(new Set())}
        data={tableData
          .filter(({ id }) => selectedIds.has(id))
          .map(
            ({ id, role, details }) =>
              ({
                id,
                role,
                details: details
                  ? {
                      name: details.name,
                    }
                  : undefined,
              }) as Partial<Case>
          )}
      />
      <Table
        headerRows={
          [
            {
              label: (
                <Checkbox
                  id="checkAll"
                  data-testid="checkAll"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onSelectAllClick}
                />
              ),
            },
            { label: 'Name', sortable: true, sortKey: 'name' },
            { label: 'Date Created', sortable: true, sortKey: 'dateCreated' },
            ...[
              {
                label: 'Sector',

                showItem: getSavedColumnById('sector')?.display,
                order: getSavedColumnById('sector')?.order,
                id: 'sector',
              },
              {
                label: (
                  <Typography
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    fontWeight={700}
                  >
                    Fit Score
                    {!survey_responses && (
                      <Tooltip
                        title="Fit score survey is incomplete. Click here to fill in the survey."
                        onClick={() => {
                          //parse which environment from the url: app.tunedd.ai = prod, stge.app.tunedd.ai = stage, dev.app.tunedd.ai = dev
                          const url = window.location.href;
                          const environment = url.includes('stage.app.tunedd.ai')
                            ? 'stage'
                            : url.includes('app.tunedd.ai')
                              ? 'prod'
                              : 'dev';
                          if (userId && org_id) {
                            triggerFitScoreSurvey(org_id, userId, environment);
                          }
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="small"
                          style={{ marginLeft: 4 }}
                          data-testid="fit-score-info-icon"
                        />
                      </Tooltip>
                    )}
                  </Typography>
                ),

                sortable: true,
                sortKey: 'fitScore',
                showItem: getSavedColumnById('fitScore')?.display,
                order: getSavedColumnById('fitScore')?.order,
                id: 'fitScore',
              },
              {
                label: 'USP',

                showItem: getSavedColumnById('ups')?.display,
                order: getSavedColumnById('ups')?.order,
                id: 'ups',
              },
              {
                label: 'Tech',

                showItem: getSavedColumnById('tech')?.display,
                order: getSavedColumnById('tech')?.order,
                id: 'tech',
              },
              {
                label: 'Patents',

                sortable: true,
                sortKey: 'patents',
                showItem: getSavedColumnById('patents')?.display,
                order: getSavedColumnById('patents')?.order,
                id: 'patents',
              },
              {
                label: 'Team',

                showItem: getSavedColumnById('team')?.display,
                order: getSavedColumnById('team')?.order,
                id: 'team',
              },
              {
                label: 'Data Coverage',

                sortable: true,
                sortKey: 'dataCoverage',
                showItem: getSavedColumnById('dataCoverage')?.display,
                order: getSavedColumnById('dataCoverage')?.order,
                id: 'dataCoverage',
              },
              {
                label: 'Risks',

                sortable: true,
                sortKey: 'risks',
                showItem: getSavedColumnById('risks')?.display,
                order: getSavedColumnById('risks')?.order,
                id: 'risks',
              },
            ]
              .filter(col => col.showItem !== false)
              .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)),
          ].flat() as HeaderCellProps[]
        }
        data={dataWithSelection}
        loading={loading}
        noDataMessage={
          !loading && tableColData.length === 0 ? 'You have no projects at the moment.' : undefined
        }
        enableSorting={true}
        defaultSortConfig={defaultSortConfig}
        stickyHeader={true}
      />
    </Box>
  );
}

export default ProjectsTable;
